/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * «Khutbah» (MKK adoption A1, owner 2026-10-24): who gives the khutbah, in which language, on what, every Friday.
 * The next eight Fridays are prefilled lines to fill in, each edited in place and saved on its own; a second Jumu'ah or
 * an Eid day is an extra line. «Vis i appen» decides whether the line is shown in the app and on the screens; the note
 * is for staff only and is never shown anywhere else. Past lines are history, below. Rules (slots, limits, "a line with
 * nothing to show is not saved") are core khutbah.js, through core.ts; the server checks again.
 */
import React, { useState } from 'react';
import { errText, send, type Body } from '../lib/api';
import { useLoad, useMsg } from '../lib/hooks';
import { useDirty } from '../lib/nav';
import { shortDate } from '../lib/format';
import { Body as CardBody, Btn, Card, Field, LoadError, Loading, Msg, Tag, TwoStep } from '../components/ui';
import { KhutbahCore } from '../core';

const PATH = '/khutbah';

export interface KhutbahDraft { day: string; slot: string; topic: string; khatib: string; language: string; notes: string; visible: boolean }
const keyOf = (r: Body) => `${r.day}|${r.slot}`;
export const draftOf = (r: Body | null, day: string, slot = 'jumua'): KhutbahDraft => ({
  day: r ? r.day : day, slot: r ? r.slot : slot, topic: (r && r.topic) || '', khatib: (r && r.khatib) || '',
  language: (r && r.language) || '', notes: (r && r.notes) || '', visible: r ? Boolean(r.visible) : true,
});
/** What the server is sent: the whole row, empty text as null. */
export const bodyOf = (d: KhutbahDraft) => ({ day: d.day, slot: d.slot, topic: d.topic || null, khatib: d.khatib || null,
  language: d.language || null, notes: d.notes || null, visible: d.visible });
const same = (a: KhutbahDraft, b: KhutbahDraft) => JSON.stringify(a) === JSON.stringify(b);

/**
 * The lines the plan shows: every Friday the panel prefills (a saved row or an empty line), every other upcoming saved
 * row (a second Jumu'ah, an Eid day), oldest first, the Jumu'ah before the second and Eid within a day.
 */
export function upcomingLines(fridays: string[], items: Body[], today: string): { key: string; saved: Body | null; day: string; slot: string }[] {
  const byKey = new Map(items.map((r) => [keyOf(r), r]));
  const keys = new Set<string>(fridays.map((d) => `${d}|jumua`));
  for (const r of items) if (r.day >= today) keys.add(keyOf(r));
  const rank = (s: string) => KhutbahCore.SLOTS.indexOf(s);
  return [...keys].map((k) => { const [day, slot] = k.split('|'); return { key: k, saved: byKey.get(k) || null, day, slot }; })
    .sort((a, b) => a.day.localeCompare(b.day) || rank(a.slot) - rank(b.slot));
}

export function Khutbah() {
  const { data, error, reload } = useLoad<Body>(PATH);
  const [drafts, setDrafts] = useState<Record<string, KhutbahDraft>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [extra, setExtra] = useState<{ day: string; slot: string }>({ day: '', slot: 'eid' });
  const [msg, say] = useMsg();
  const dirtyKeys = Object.keys(drafts);
  useDirty('khutbah', dirtyKeys.length > 0);
  if (error) return <LoadError error={error} retry={reload} />;
  if (!data) return <Loading />;

  const items: Body[] = data.items || [];
  const today: string = data.today;
  const write = Boolean(data.canWrite);
  const lines = upcomingLines(data.fridays || [], items, today);
  const history = items.filter((r) => r.day < today).sort((a, b) => b.day.localeCompare(a.day));
  const current = (l: { key: string; saved: Body | null; day: string; slot: string }) => drafts[l.key] || draftOf(l.saved, l.day, l.slot);
  const change = (l: { key: string; saved: Body | null; day: string; slot: string }, patch: Partial<KhutbahDraft>) => {
    const next = { ...current(l), ...patch };
    setDrafts((ds) => {
      const out = { ...ds };
      if (same(next, draftOf(l.saved, l.day, l.slot))) delete out[l.key]; else out[l.key] = next;
      return out;
    });
  };
  const forget = (key: string) => setDrafts((ds) => { const out = { ...ds }; delete out[key]; return out; });

  async function save(key: string, d: KhutbahDraft) {
    const c = KhutbahCore.input(bodyOf(d));
    if (!c.ok) return say(Object.values(c.errors)[0], 'err');
    setBusy(key);
    const r = await send(PATH, { method: 'PUT', body: JSON.stringify(bodyOf(d)) });
    setBusy(null);
    if (!r.ok) return say((r.body && r.body.message) || errText(r.body), 'err');
    forget(key);
    say(`Lagret for ${shortDate(d.day)}. ${d.visible ? 'Appen og skjermene viser det ved neste oppdatering.' : 'Vises ikke i appen.'}`, 'ok');
    await reload();
  }
  async function remove(day: string, slot: string) {
    const r = await send(`${PATH}/${encodeURIComponent(day)}/${encodeURIComponent(slot)}`, { method: 'DELETE' });
    if (!r.ok) return say(errText(r.body), 'err');
    forget(`${day}|${slot}`);
    say(`Fjernet for ${shortDate(day)}.`, 'ok');
    await reload();
  }
  function addExtra() {
    if (!extra.day) return say('Velg en dag.', 'err');
    if (lines.some((l) => l.day === extra.day && l.slot === extra.slot)) return say('Den linjen finnes allerede.', 'err');
    setDrafts((ds) => ({ ...ds, [`${extra.day}|${extra.slot}`]: draftOf(null, extra.day, extra.slot) }));
    setExtra({ day: '', slot: 'eid' });
  }
  // A line added by hand (an Eid day) lives only as a draft until it is saved.
  const extraLines = dirtyKeys.filter((k) => !lines.some((l) => l.key === k)).map((k) => { const [day, slot] = k.split('|'); return { key: k, saved: null, day, slot }; });
  const shown = [...lines, ...extraLines].sort((a, b) => a.day.localeCompare(b.day));

  return (
    <>
      <Card title="Khutbah-plan" id="khutbah-card"
            hint="Hvem som holder khutbah, på hvilket språk og om hva — de neste fredagene. Lagre hver linje for seg."
            more={<>Det som er krysset av for «Vis i appen», vises i appen og på skjermene fra uken før: denne fredagen fram til asr,
              deretter neste. Notatet er bare for de ansatte og vises aldri for medlemmene. En linje uten tema, khatib og språk lagres ikke.</>}>
        <CardBody>
          <table className="t kh-plan">
            <thead><tr><th scope="col">Dag</th><th scope="col">Tema</th><th scope="col">Khatib</th><th scope="col">Språk</th>
              <th scope="col">Notat (bare ansatte)</th><th scope="col">Vis i appen</th><th scope="col"><span className="vh">Handling</span></th></tr></thead>
            <tbody>
              {shown.map((l) => {
                const d = current(l);
                const dirty = Boolean(drafts[l.key]);
                const label = `${shortDate(l.day)} ${KhutbahCore.SLOT_NO[l.slot] || l.slot}`;
                return (
                  <tr key={l.key}>
                    <td>{shortDate(l.day)}{l.slot !== 'jumua' && <div><Tag tone="gold">{KhutbahCore.SLOT_NO[l.slot] || l.slot}</Tag></div>}
                      {!l.saved && !dirty && <div className="note">ikke fylt inn</div>}</td>
                    <td><Field label={<span className="vh">Tema, {label}</span>} className="field">{(id) =>
                      <input id={id} type="text" maxLength={KhutbahCore.MAX_TEXT} disabled={!write} value={d.topic} onChange={(e) => change(l, { topic: e.target.value })} />}</Field></td>
                    <td><Field label={<span className="vh">Khatib, {label}</span>} className="field">{(id) =>
                      <input id={id} type="text" maxLength={KhutbahCore.MAX_TEXT} disabled={!write} value={d.khatib} onChange={(e) => change(l, { khatib: e.target.value })} />}</Field></td>
                    <td><Field label={<span className="vh">Språk, {label}</span>} className="field">{(id) =>
                      <input id={id} type="text" maxLength={KhutbahCore.MAX_TEXT} disabled={!write} placeholder="Urdu, Norsk …" value={d.language} onChange={(e) => change(l, { language: e.target.value })} />}</Field></td>
                    <td><Field label={<span className="vh">Notat, {label}</span>} className="field">{(id) =>
                      <textarea id={id} rows={1} maxLength={KhutbahCore.MAX_NOTES} disabled={!write} value={d.notes} onChange={(e) => change(l, { notes: e.target.value })} />}</Field></td>
                    <td><label className="chk"><input type="checkbox" disabled={!write} checked={d.visible} onChange={(e) => change(l, { visible: e.target.checked })} />
                      <span className="vh">Vis i appen, {label}</span></label></td>
                    <td>{write && <>
                      <Btn disabled={!dirty || busy === l.key} onClick={() => save(l.key, d)}>{busy === l.key ? 'Lagrer …' : 'Lagre'}</Btn>{' '}
                      {dirty && <Btn ghost onClick={() => forget(l.key)}>Angre</Btn>}{' '}
                      {l.saved && <TwoStep label="Fjern" confirmLabel="Klikk igjen for å fjerne" onConfirm={() => remove(l.day, l.slot)} />}
                    </>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {write && (
            <div className="row" style={{ marginTop: 12, alignItems: 'flex-end' }}>
              <Field label="Ekstra linje: dag" className="field half">{(id) =>
                <input id={id} type="date" value={extra.day} onChange={(e) => setExtra({ ...extra, day: e.target.value })} />}</Field>
              <Field label="Hva" className="field half">{(id) =>
                <select id={id} value={extra.slot} onChange={(e) => setExtra({ ...extra, slot: e.target.value })}>
                  {KhutbahCore.SLOTS.map((s) => <option key={s} value={s}>{KhutbahCore.SLOT_NO[s]}</option>)}
                </select>}</Field>
              <Btn ghost onClick={addExtra}>+ Legg til linje</Btn>
            </div>
          )}
          {!write && <p className="note">Du kan se planen, men ikke endre den.</p>}
          <Msg text={msg.text} tone={msg.tone} />
        </CardBody>
      </Card>
      <Card title="Tidligere" hint="Khutbah-planen for dager som er passert. Vises bare her.">
        <table className="t kh-history">
          <thead><tr><th scope="col">Dag</th><th scope="col">Khutbah</th><th scope="col">Notat</th><th scope="col">Vist</th><th scope="col"><span className="vh">Handling</span></th></tr></thead>
          <tbody>
            {history.length ? history.map((r) => (
              <tr key={keyOf(r)}>
                <td>{shortDate(r.day)}{r.slot !== 'jumua' && <> <Tag tone="gold">{KhutbahCore.SLOT_NO[r.slot] || r.slot}</Tag></>}</td>
                <td>{KhutbahCore.line(r) || '—'}</td>
                <td className="note">{r.notes || ''}</td>
                <td>{r.visible ? <Tag tone="green">ja</Tag> : <Tag tone="grey">nei</Tag>}</td>
                <td>{write && <TwoStep label="Fjern" confirmLabel="Klikk igjen for å fjerne" onConfirm={() => remove(r.day, r.slot)} />}</td>
              </tr>
            )) : <tr><td colSpan={5}>Ingen tidligere linjer de siste tre månedene.</td></tr>}
          </tbody>
        </table>
      </Card>
    </>
  );
}
